'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import Header from './Header'
import Link from 'next/link'
import RoleDecoration from '@/components/role/RoleDecoration'

export default function ConditionalLayout() {
  const pathname = usePathname()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [hasEntitlement, setHasEntitlement] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const authenticated = !!session
        setIsAuthenticated(authenticated)
        
        // 如果已登入，檢查是否有訂閱權益
        if (authenticated && session?.user) {
          try {
            const res = await fetch('/api/me/entitlement', { 
              cache: 'no-store',
              credentials: 'include'
            })
            if (res.ok) {
              const data = await res.json()
              const entitlement = data.entitlement
              const now = Date.now()
              const hasValidSubscription = entitlement?.endAt && new Date(entitlement.endAt).getTime() > now
              setHasEntitlement(hasValidSubscription && entitlement.tier !== 'None')
            } else {
              setHasEntitlement(false)
            }
          } catch (error) {
            setHasEntitlement(false)
          }
        } else {
          setHasEntitlement(false)
        }
      } catch (error) {
        setIsAuthenticated(false)
        setHasEntitlement(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()

    // 監聽認證狀態變化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const authenticated = !!session
      setIsAuthenticated(authenticated)
      
      // 如果已登入，檢查是否有訂閱權益
      if (authenticated && session?.user) {
        try {
          const res = await fetch('/api/me/entitlement', { 
            cache: 'no-store',
            credentials: 'include'
          })
          if (res.ok) {
            const data = await res.json()
            const entitlement = data.entitlement
            const now = Date.now()
            const hasValidSubscription = entitlement?.endAt && new Date(entitlement.endAt).getTime() > now
            setHasEntitlement(hasValidSubscription && entitlement.tier !== 'None')
          } else {
            setHasEntitlement(false)
          }
        } catch (error) {
          setHasEntitlement(false)
        }
      } else {
        setHasEntitlement(false)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, []) // supabase 是穩定的單例，不需要作為依賴

  // 首頁和 try 頁面不顯示 Header 和 Footer
  const isHomePage = pathname === '/'
  const isTryPage = pathname?.startsWith('/try/')
  
  // 如果正在載入、首頁或 try 頁面，不顯示 Header
  if (isLoading || isHomePage || isTryPage) {
    return null
  }

  // 其他頁面：只在登入時顯示 Header
  // Header 包含需要登入的連結，所以只對登入用戶顯示
  // 角色裝飾：只在有訂閱權益時顯示
  if (isAuthenticated) {
    return (
      <>
        <Header />
        {hasEntitlement && <RoleDecoration />}
      </>
    )
  }

  return null
}

// Footer 組件（單獨導出，用於 layout）
export function ConditionalFooter() {
  const pathname = usePathname()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setIsAuthenticated(!!session)
      } catch (error) {
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, []) // supabase 是穩定的單例，不需要作為依賴

  const isHomePage = pathname === '/'
  
  // 如果正在載入或是首頁，不顯示 Footer
  // Footer 對所有登入和未登入用戶都顯示（除了首頁）
  if (isLoading || isHomePage) {
    return null
  }

  return (
    <footer className="bg-gray-800 text-white py-4 md:py-6 mt-8">
      <div className="container mx-auto px-4">
        {/* 手機版：垂直排列 */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0">
          <div className="text-center md:text-left">
            <p className="text-sm md:text-base whitespace-nowrap">
              &copy; {new Date().getFullYear()} 君王資訊科技有限公司
            </p>
          </div>
          <div className="flex justify-center md:justify-end space-x-4 md:space-x-6">
            <Link 
              href="/contact" 
              className="hover:text-blue-300 transition-colors text-sm md:text-base whitespace-nowrap"
            >
              聯絡我們
            </Link>
            <Link 
              href="/copyright" 
              className="hover:text-blue-300 transition-colors text-sm md:text-base whitespace-nowrap"
            >
              版權聲明
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

